import { AccessDeniedPage } from '@/app/pages/AccessDeniedPage'
import {
	AssetsPage,
	DashboardDisplayPage,
	DashboardsPage,
	DbcParserManagementPage,
	DeviceManagementPage,
	ExportersPage,
	OtaCampaignsPage,
	RemoteCommandsPage,
	RulesPage,
	SessionRulesPage,
	SessionsFeedPage,
	SystemNotificationsPage,
	TerminalPage,
} from '@/app/routes/lazyPages'
import { organizationRoute } from '@/app/routes/organizationRoutes'
import { statusParamsSchema, tenantParamsSchema } from '@/app/routes/params'
import { projectSettingsRouteTree } from '@/app/routes/projectSettingsRoutes'
import { AccessDeniedFallback, RouteAccessBoundary } from '@/components/access-control'
import { authenticatedRouteGuard, getProjectFallbackTarget, prefetchQuota, redirectToFirstOrg } from '@/lib/routeGuards'
import { resolveOrgId, resolveTenantId } from '@/lib/routeScope'
import { getDefaultRouteForUser } from '@/lib/routerUtils'
import {
	accessDeniedSearchSchema,
	dashboardSearchSchema,
	dbcParsersSearchSchema,
	deviceSearchSchema,
	sessionRulesSearchSchema,
} from '@/lib/searchSchemas'
import { isPlatformUser, userBelongsToTenant } from '@/stores/auth'
import { useAuthStore } from '@/stores/authStore'
import { Outlet, createRoute, redirect } from '@tanstack/react-router'
import { z } from 'zod'

/**
 * Project scoped routes, everything under `/orgs/$orgId/projects/$tenantId`.
 *
 * The project route itself validates tenant membership and primes the quota cache; the modules below
 * are the sections the sidebar lists. Project settings live in their own module.
 */
export const organizationProjectRoute = createRoute({
	getParentRoute: () => organizationRoute,
	path: '/projects/$tenantId',
	parseParams: params => {
		const { tenantId } = params
		return { tenantId: tenantParamsSchema.parse({ tenantId }).tenantId }
	},
	component: () => <Outlet />,
	beforeLoad: ({ context, params, location }) => {
		authenticatedRouteGuard(context, { location })
		if (!params.tenantId || params.tenantId.trim() === '') {
			throw redirectToFirstOrg()
		}

		const orgId = resolveOrgId(params.orgId)
		const tenantId = resolveTenantId(params.tenantId)
		const user = useAuthStore.getState().user
		const platformUser = user && isPlatformUser(user) ? user : null

		if (platformUser && !userBelongsToTenant(platformUser, tenantId)) {
			throw redirect({ ...getProjectFallbackTarget(platformUser, orgId, tenantId), replace: true })
		}

		useAuthStore.getState().setCurrentTenant(tenantId)
	},
	// Prime the quota cache so access-control hooks have fresh data on first render.
	loader: ({ params }) => prefetchQuota(resolveTenantId(params.tenantId)),
})

export const projectHomeRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/',
	component: () => <AccessDeniedFallback />,
	beforeLoad: ({ context, params }) => {
		authenticatedRouteGuard(context)

		// This route matches the bare project URL, so the landing route is always somewhere else.
		// Segments, not resolved ids: this builds a URL rather than asking the API about one.
		const defaultRoute = getDefaultRouteForUser(params.tenantId, params.orgId)
		if (defaultRoute) {
			throw redirect({ to: defaultRoute, replace: true })
		}
	},
})

// Named access-denied page — org-ownership and bootstrap edge cases only.
export const accessDeniedRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/access-denied',
	validateSearch: accessDeniedSearchSchema,
	component: () => <AccessDeniedPage />,
})

export const devicesRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/device-management',
	component: () => (
		<RouteAccessBoundary resource='devices'>
			<Outlet />
		</RouteAccessBoundary>
	),
})

export const devicesIndexRoute = createRoute({
	getParentRoute: () => devicesRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/device-management/$status',
			params: { ...params, status: 'all' },
			search,
			replace: true,
		})
	},
})

// Device management with status parameter
export const devicesStatusRoute = createRoute({
	getParentRoute: () => devicesRoute,
	path: '/$status',
	parseParams: params => statusParamsSchema.parse(params),
	validateSearch: deviceSearchSchema,
	component: () => <DeviceManagementPage />,
})

// DASHBOARDS (under Data section)
export const dashboardTypeSchema = z.object({
	type: z.enum(['all', 'device', 'fleet']),
})

export const dashboardsRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/data/dashboards',
	component: () => (
		<RouteAccessBoundary resource='dashboards'>
			<Outlet />
		</RouteAccessBoundary>
	),
})

// Index route for dashboards that redirects to all
export const dashboardsIndexRoute = createRoute({
	getParentRoute: () => dashboardsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/data/dashboards/$type',
			params: { ...params, type: 'all' },
			search,
			replace: true,
		})
	},
})

// Dashboard with type parameter
export const dashboardsTypeRoute = createRoute({
	getParentRoute: () => dashboardsRoute,
	path: '/$type',
	parseParams: params => dashboardTypeSchema.parse(params),
	validateSearch: dashboardSearchSchema,
	component: () => <DashboardsPage />,
})

// Dashboard Display route - for showing dashboard panels with selected device OR device selector
export const dashboardDisplayParamsSchema = z.object({
	dashboardId: z.string(),
	deviceId: z.string(),
})

export const dashboardDisplayRoute = createRoute({
	getParentRoute: () => dashboardsRoute,
	path: '/view/$dashboardId/$deviceId',
	parseParams: params => dashboardDisplayParamsSchema.parse(params),
	component: () => <DashboardDisplayPage />,
	beforeLoad: ({ context }) => {
		authenticatedRouteGuard(context)
	},
})

// DBC PARSERS (under Data section)
export const dbcParserTypeSchema = z.object({
	type: z.enum(['all', 'custom', 'obd', 'j1939']),
})

export const dbcParsersRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/data/dbc-parsers',
	component: () => (
		<RouteAccessBoundary resource='dbc_parsers'>
			<Outlet />
		</RouteAccessBoundary>
	),
})

// Index route for dbc-parsers that redirects to all
export const dbcParsersIndexRoute = createRoute({
	getParentRoute: () => dbcParsersRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/data/dbc-parsers/$type',
			params: { ...params, type: 'all' },
			search,
			replace: true,
		})
	},
})

// DBC parsers with type parameter
export const dbcParsersTypeRoute = createRoute({
	getParentRoute: () => dbcParsersRoute,
	path: '/$type',
	parseParams: params => dbcParserTypeSchema.parse(params),
	validateSearch: dbcParsersSearchSchema,
	component: () => <DbcParserManagementPage />,
})

export const exportersRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/data/exporters',
	component: () => (
		<RouteAccessBoundary resource='exporters'>
			<Outlet />
		</RouteAccessBoundary>
	),
})

export const exportersIndexRoute = createRoute({
	getParentRoute: () => exportersRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/data/exporters/$type',
			params: { ...params, type: 'all' },
			search,
			replace: true,
		})
	},
})

// Exporters with type parameter
export const exportersTypeRoute = createRoute({
	getParentRoute: () => exportersRoute,
	path: '/$type',
	component: () => <ExportersPage />,
})

// TERMINAL (under Data section)
export const terminalRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/data/terminal',
	component: () => <TerminalPage />,
})

// ACTION RULES
export const rulesRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/actions/rules',
	component: () => <Outlet />,
})

// Index route for rules that redirects to all
export const rulesIndexRoute = createRoute({
	getParentRoute: () => rulesRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/actions/rules/$status',
			params: { ...params, status: 'all' },
			search,
			replace: true,
		})
	},
})

export const rulesStatusRoute = createRoute({
	getParentRoute: () => rulesRoute,
	path: '/$status',
	component: () => <RulesPage />,
})

export const remoteCommandsRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/actions/remote-commands',
	component: () => <Outlet />,
})

// Index route for remote commands that redirects to all
export const remoteCommandsIndexRoute = createRoute({
	getParentRoute: () => remoteCommandsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/actions/remote-commands/$type',
			params: { ...params, type: 'all' },
			search,
			replace: true,
		})
	},
})

export const remoteCommandsTypeRoute = createRoute({
	getParentRoute: () => remoteCommandsRoute,
	path: '/$type',
	component: () => <RemoteCommandsPage />,
})

export const otaCampaignsRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/actions/ota-campaigns',
	component: () => <Outlet />,
})

export const otaCampaignsIndexRoute = createRoute({
	getParentRoute: () => otaCampaignsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/actions/ota-campaigns/$type',
			params: { ...params, type: 'all' },
			search,
			replace: true,
		})
	},
})

export const otaCampaignsTypeRoute = createRoute({
	getParentRoute: () => otaCampaignsRoute,
	path: '/$type',
	component: () => <OtaCampaignsPage />,
})

// ASSETS
export const assetsRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/actions/assets',
	component: () => <Outlet />,
})

// Index route for assets that redirects to files
export const assetsIndexRoute = createRoute({
	getParentRoute: () => assetsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/actions/assets/$type',
			params: { ...params, type: 'files' },
			search,
			replace: true,
		})
	},
})

// Assets with type parameter
export const assetsTypeRoute = createRoute({
	getParentRoute: () => assetsRoute,
	path: '/$type',
	component: () => <AssetsPage />,
})

// MONITORING ALERT RULES (under Monitoring section)
export const sessionRuleTypeSchema = z.object({
	type: z.enum(['all', 'no-signal', 'signal-based']),
})

export const sessionsRulesRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/sessions/rules',
	component: () => <Outlet />,
})

// Index route for session rules that redirects to all
export const sessionsRulesIndexRoute = createRoute({
	getParentRoute: () => sessionsRulesRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/sessions/rules/$type',
			params: { ...params, type: 'all' },
			search,
			replace: true,
		})
	},
})

// Session rules with type parameter
export const sessionsRulesTypeRoute = createRoute({
	getParentRoute: () => sessionsRulesRoute,
	path: '/$type',
	parseParams: params => sessionRuleTypeSchema.parse(params),
	validateSearch: sessionRulesSearchSchema,
	component: () => <SessionRulesPage />,
})

// Sessions dashboard route
export const sessionsDashboardRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/sessions/dashboard',
	component: () => <Outlet />,
})

// Index route for sessions dashboard that redirects to all
export const sessionsDashboardIndexRoute = createRoute({
	getParentRoute: () => sessionsDashboardRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/sessions/dashboard/$type',
			params: { ...params, type: 'all' },
			search,
			replace: true,
		})
	},
})

export const sessionsDashboardTypeSchema = z.object({
	// 'all' or a session rule name (URL-decoded by the router)
	type: z.string().min(1),
})

// Sessions dashboard with type parameter
export const sessionsDashboardTypeRoute = createRoute({
	getParentRoute: () => sessionsDashboardRoute,
	path: '/$type',
	parseParams: params => sessionsDashboardTypeSchema.parse(params),
	component: () => <SessionsFeedPage />,
})

// SYSTEM NOTIFICATIONS
export const systemNotificationsTypeSchema = z.object({
	type: z.enum(['all', 'dbc', 'exporters', 'notifications']),
})

export const systemNotificationsRoute = createRoute({
	getParentRoute: () => organizationProjectRoute,
	path: '/system-notifications',
	component: () => <Outlet />,
})

export const systemNotificationsIndexRoute = createRoute({
	getParentRoute: () => systemNotificationsRoute,
	path: '/',
	beforeLoad: ({ params, search }) => {
		throw redirect({
			to: '/orgs/$orgId/projects/$tenantId/system-notifications/$type',
			params: { ...params, type: 'all' },
			search,
			replace: true,
		})
	},
})

export const systemNotificationsTypeRoute = createRoute({
	getParentRoute: () => systemNotificationsRoute,
	path: '/$type',
	parseParams: params => systemNotificationsTypeSchema.parse(params),
	component: () => <SystemNotificationsPage />,
})

/** Everything under a project URL, including its settings subtree. */
export const organizationProjectRouteTree = organizationProjectRoute.addChildren([
	projectHomeRoute,
	accessDeniedRoute,
	devicesRoute.addChildren([devicesIndexRoute, devicesStatusRoute]),
	dashboardsRoute.addChildren([dashboardsIndexRoute, dashboardsTypeRoute, dashboardDisplayRoute]),
	dbcParsersRoute.addChildren([dbcParsersIndexRoute, dbcParsersTypeRoute]),
	exportersRoute.addChildren([exportersIndexRoute, exportersTypeRoute]),
	terminalRoute,
	rulesRoute.addChildren([rulesIndexRoute, rulesStatusRoute]),
	remoteCommandsRoute.addChildren([remoteCommandsIndexRoute, remoteCommandsTypeRoute]),
	otaCampaignsRoute.addChildren([otaCampaignsIndexRoute, otaCampaignsTypeRoute]),
	assetsRoute.addChildren([assetsIndexRoute, assetsTypeRoute]),
	sessionsRulesRoute.addChildren([sessionsRulesIndexRoute, sessionsRulesTypeRoute]),
	sessionsDashboardRoute.addChildren([sessionsDashboardIndexRoute, sessionsDashboardTypeRoute]),
	systemNotificationsRoute.addChildren([systemNotificationsIndexRoute, systemNotificationsTypeRoute]),
	projectSettingsRouteTree,
])
